// web/src/components/lifecycle/MasterUnlockScreen.tsx
//
// Returning-user master-password unlock. Routes at `/master` and is
// reached either from `RootRedirect` (existing OPFS vault probe) or
// via a direct URL.
//
// Layout:
//   - Unlock heading
//   - Single password input (autofocus), submit button
//   - "Wipe app" secondary button (destructive)
//
// Submit pipeline:
//   1. toPassphraseBytes → openOpfsVault on the origin-root vault
//   2. loadAppConfig (the encrypted blob at /system/config.enc)
//   3. seed masterSession + session stores
//   4. navigate to /v/:id/~/ (or `returnTo` if present)
//   The Uint8Array is zeroized in `finally`; the string state is
//   cleared before navigate.
//
// Wipe flow:
//   Click "Wipe app" → WipeConfirmation gate (typed "WIPE") → clear
//   master/session stores, call `wipeOpfs()`, navigate to /setup/1.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useVault } from "../../hooks/useVault";
import { useMasterSession } from "../../stores/masterSession";
import { useSession } from "../../stores/session";
import { toPassphraseBytes, zeroize } from "../../lib/passphraseBytes";
import { wipeOpfs } from "../../lib/wipeOpfs";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { WipeConfirmation } from "./WipeConfirmation";

// Primary OPFS vault subdir — empty string maps to the OPFS origin
// root, which is where MasterSetupWizard places it.
const OPFS_SUBDIR = "";

type View = "unlock" | "wipe-confirm" | "wiping";

export function MasterUnlockScreen() {
  const vault = useVault();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo");

  const setSession = useSession((s) => s.setSession);
  const setMaster = useMasterSession((s) => s.setMaster);

  const [passphrase, setPassphrase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorKind, setErrorKind] = useState<
    | { kind: "wrong-password" }
    | { kind: "generic"; details: string }
    | null
  >(null);
  const [view, setView] = useState<View>("unlock");
  const [wipeError, setWipeError] = useState<string | null>(null);
  const passInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    passInputRef.current?.focus();
  }, []);

  async function onUnlock(): Promise<void> {
    setSubmitting(true);
    setErrorKind(null);
    const bytes = toPassphraseBytes(passphrase);
    try {
      const handle = await vault.openOpfsVault(OPFS_SUBDIR, bytes);

      // Load the app-config; on a healthy vault this always succeeds.
      // A missing or corrupt config surfaces as a generic error and
      // the user's recovery is the "Wipe app" button.
      const appConfig = await vault.loadAppConfig(handle);

      const unlockedAt = Date.now();
      setMaster({ masterHandle: handle, appConfig, unlockedAt });

      // Resolve the primary vault id + display name from the
      // app-config. If the config is missing (shouldn't happen on a
      // healthy install), fall back to the worker's display name and
      // a synthetic id so the URL is at least navigable.
      const primaryId = appConfig?.primary_vault_id ?? "primary";
      const primaryEntry = appConfig?.vaults.find(
        (v) => v.id === primaryId,
      );
      let displayName = primaryEntry?.display_name ?? "primary";
      if (!primaryEntry) {
        try {
          displayName = await vault.getDisplayName(handle);
        } catch {
          // Non-fatal: UI falls back to "primary".
        }
      }

      setSession({
        handle,
        vaultId: primaryId,
        displayName,
        unlockedAt,
      });

      setPassphrase("");
      navigate(returnTo ?? `/v/${primaryId}/~/`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("wrong password")) {
        setErrorKind({ kind: "wrong-password" });
        passInputRef.current?.focus();
      } else {
        setErrorKind({ kind: "generic", details: msg });
      }
      setSubmitting(false);
    } finally {
      zeroize(bytes);
    }
  }

  async function onWipeConfirm(): Promise<void> {
    setView("wiping");
    setWipeError(null);
    try {
      // Drop worker-side handles first so OPFS file mappings can be
      // freed before we remove the files. `lockVault` on an unknown
      // handle is a documented no-op.
      const master = useMasterSession.getState().master;
      if (master) {
        try {
          await vault.lockVault(master.masterHandle);
        } catch {
          // best-effort
        }
      }
      // Clear in-memory stores BEFORE wipeOpfs runs so that if the
      // wipe itself fails the "Continue anyway" handler can't
      // consult stale state and route back to /master.
      useMasterSession.getState().clear();
      useSession.getState().clear();

      await wipeOpfs();
      navigate("/setup/1");
    } catch (err) {
      setWipeError(err instanceof Error ? err.message : String(err));
      // Stores stay cleared — the wipe is effectively complete even
      // if OPFS has lingering junk.
    }
  }

  if (view === "wipe-confirm") {
    return (
      <div className="mx-auto min-h-[70vh] max-w-md px-5 py-10 md:px-6 md:py-12">
        <p className="mb-3 text-[13px] leading-relaxed text-text-soft">
          Permanently delete the local OPFS vault and all app settings on
          this device. S3 vaults stored in the cloud are{" "}
          <strong>not</strong> deleted, but you'll need to re-add them.
          This cannot be undone.
        </p>
        <WipeConfirmation
          expectedText="WIPE"
          onConfirm={() => {
            void onWipeConfirm();
          }}
          onCancel={() => setView("unlock")}
        />
      </div>
    );
  }

  if (view === "wiping") {
    return (
      <div className="mx-auto min-h-[70vh] max-w-md px-5 py-10 md:px-6 md:py-12">
        {wipeError === null ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="mb-3 h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            <div className="text-sm text-text-soft">Wiping app…</div>
          </div>
        ) : (
          <div className="rounded-lg border border-danger/40 bg-danger/10 p-4">
            <div className="mb-2 text-sm font-semibold text-danger">
              Wipe failed
            </div>
            <p className="mb-3 text-[13px] leading-relaxed text-text">
              {wipeError}
            </p>
            <button
              type="button"
              onClick={() => navigate("/setup/1")}
              className="rounded-md border border-border px-4 py-2 text-sm text-text-soft"
            >
              Continue anyway
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-5 py-10 pb-[calc(env(safe-area-inset-bottom)+40px)] md:px-6 md:py-12">
      <div className="mb-2 text-[11px] uppercase tracking-[0.14em] text-accent-soft">
        ↵ unlock
      </div>
      <h1 className="mb-6 text-2xl font-medium tracking-tight">
        Unlock vaultwisp
      </h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!submitting && passphrase.length > 0) void onUnlock();
        }}
      >
        <label
          htmlFor="master-unlock-passphrase"
          className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
        >
          Master passphrase
        </label>
        <PassphraseInput
          ref={passInputRef}
          id="master-unlock-passphrase"
          autoCompleteMode="current-password"
          aria-label="Master passphrase"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
        />
        {errorKind?.kind === "wrong-password" && (
          <div className="mt-1.5 text-[12px] text-danger">
            Incorrect password
          </div>
        )}
        {errorKind?.kind === "generic" && (
          <details className="mt-1.5 text-[12px] text-danger">
            <summary className="cursor-pointer">
              Could not unlock vault
            </summary>
            <div className="mt-1 font-mono text-[11px] text-text-soft">
              {errorKind.details}
            </div>
          </details>
        )}

        <button
          type="submit"
          disabled={submitting || passphrase.length === 0}
          className="mt-4 w-full rounded-md bg-accent px-4 py-3 text-base font-medium text-white disabled:opacity-40 md:py-2 md:text-sm"
        >
          {submitting ? "Unlocking…" : "Unlock"}
        </button>
      </form>

      <div className="mt-4 flex items-center justify-end text-[11px]">
        <button
          type="button"
          onClick={() => setView("wipe-confirm")}
          className="text-text-dim underline hover:text-danger"
        >
          Wipe app
        </button>
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-text-dim">
        Argon2id takes about a second. The passphrase is never stored or
        cached.
      </p>
    </div>
  );
}
